import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import useApi from './api';
import ClientsDB from './clientsDB';
import { permissions, roles } from './permissions';
import { hasRoutePermission } from './routePermissions';

const AuthContext = React.createContext(null);

const USER_COMPANY = 'user-company';

function AuthProvider({ children }) {
    const [user, setUser] = React.useState(loadCredentials());
    const [company, setCompany] = React.useState(loadCompany());
    const api = useApi();

    const SAVE_COMPANY = true;

    /**
     * Saves user credentials info to local storage.
     * @param {object} credentials User credentials.
     */
    function saveCredentials(credentials) {
        localStorage.setItem('access_token', credentials.access_token);
        localStorage.setItem('refresh_token', credentials.refresh_token);
    }

    /**
     * Loads user credentials from local storage.
     * @returns {object} User credentials.
     */
    function loadCredentials() {
        try {
            const access_token = localStorage.getItem('access_token');

            if (access_token) {
                return { access_token };
            }

            if (!localStorage.getItem('refresh_token')) {
                localStorage.clear();
            }

            return null;
        } catch (e) {
            return null;
        }
    }

    function saveCompany(company) {
        localStorage.setItem(USER_COMPANY, JSON.stringify(company));
    }

    /**
     * Loads company information from localStorage.
     * @returns {object} company info {id: string, name: string}
     */
    function loadCompany() {
        try {
            return JSON.parse(localStorage.getItem(USER_COMPANY));
        } catch (e) {
            return null;
        }
    }

    /**
     * Remove company info from localStorage.
     */
    function removeCompany() {
        localStorage.removeItem(USER_COMPANY);
    }

    /**
     * Remove user credentials from local storage.
     */
    function removeCredetials() {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
    }

    function removeDB() {
        const db = new ClientsDB();
        db.deleteDatabase();
    }

    /**
     * Sign in user with specified credentials.
     * @param {object} credentials User email and password.
     */
    function signin(credentials) {
        return new Promise((resolve, reject) => {
            api.login(credentials).then(response => {
                if (response.status === 200) {
                    response.json().then((data) => {
                        const { access_token, refresh_token } = data;

                        if (access_token && refresh_token) {
                            saveCredentials(data);
                            setUser(user => ({ ...user, access_token }));
                            resolve(data);
                        } else {
                            reject(new TypeError('Cannot get access and refresh tokens.'));
                        }
                    }).catch(error => {
                        reject(error);
                    });
                } else {
                    response.json().then(data => {
                        reject(new Error(data.detail));
                    }).catch((error) => {
                        reject(error);
                    });
                }
            }).catch(error => {
                reject(error);
            });
        });
    }

    /**
     * Sign out user and remove credentials from localStorage.
     * @param {function} callback Function to trigger aftet logout.
     */
    function signout(callback) {
        setUser(null);
        setCompany(null);
        removeCredetials();
        removeDB();

        if (typeof callback === 'function') {
            callback();
        }
    }

    /**
     * Update authorization tokens after call to backend failed.
     * @param {object} data New credentials with access and refresh tokens.
     */
    function refreshAuth(data) {
        saveCredentials(data);
        const { access_token } = data;
        setUser(user => ({ ...user, access_token }));
    }

    /**
     * Select current user company.
     * @param {object} company Company info
     * @param {function} callback Callback to call after company selection.
     */
    function selectCompany(company, callback) {
        if (SAVE_COMPANY) {
            saveCompany(company);
        }

        setCompany(company);

        if (typeof callback === 'function') {
            callback();
        }
    }

    /**
     * Refresh company parameters (i.e. name).
     * @param {object} settings Company settings.
     */
    function refreshCompanySettings(settings) {
        setCompany(company => ({ ...company, name: settings.name }));
    }

    function hasPermission(forAction) {
        const roles = permissions.get(forAction);

        if (!Array.isArray(roles)) return false;

        return roles.includes(company.role);
    }

    function getRole() {
        return company?.role || roles.ALL;
    }

    const value = { user, company, removeCompany, signin, signout, refreshAuth, selectCompany, refreshCompanySettings, hasPermission, getRole };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function RouteGuard({ children }) {
    const auth = useAuth();
    const location = useLocation();

    if (!auth.user || !auth.user.access_token) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    const { pathname } = location;
    const { role } = auth.company || roles.ALL;

    if (!hasRoutePermission(pathname, role)) {
        return <Navigate to="/" replace />;
    }

    return children;
}

function useAuth() {
    return React.useContext(AuthContext);
}

RouteGuard.propTypes = {
    children: PropTypes.node,
};

AuthProvider.propTypes = {
    children: PropTypes.node,
};

export { useAuth, AuthProvider, RouteGuard };
